import * as React from 'react';
import {
  ANSWER_MAX_CHARS,
  ANSWER_MAX_DOCUMENTS,
  QUESTION_MAX_CHARS,
  QUESTION_STATUS_LABELS,
  fetchQuestionSuggestions,
  refusedDocumentIds,
  type AnswerQuestionRequest,
  type AskQuestionRequest,
  type PublishedQuestionView,
  type QuestionDocumentRef,
  type QuestionStatus,
  type QuestionSuggestionsResponse,
} from '../api/questions';
import { formatDate } from '../lib/date-only';
import { categoryPathLabel } from '../lib/period-tree';
import { useSingleSubmit } from '../lib/single-submit';
import { cn } from '../lib/utils';
import { Button } from './button';
import { ErrorText } from './error-text';
import { Icon, type IconName } from './icons';
import { Chip, type ChipTone } from './status-chip';

const STATUS_CHIP: Record<QuestionStatus, { tone: ChipTone; icon: IconName }> = {
  open: { tone: 'neutral', icon: 'help' },
  answered: { tone: 'certified', icon: 'check' },
  closed: { tone: 'na', icon: 'archive' },
};

/** A question's status (Open / Answered / Closed): word + icon + colour. */
export function QuestionStatusChip({ status }: { status: QuestionStatus }) {
  const spec = STATUS_CHIP[status];
  return (
    <Chip tone={spec.tone} icon={spec.icon}>
      {QUESTION_STATUS_LABELS[status]}
    </Chip>
  );
}

/** Shared multi-line field style (matches Input). */
export const textareaClass =
  'flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50';

const selectClass =
  'flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-50';

const docName = (name: string | null | undefined) => name || 'Untitled document';

/**
 * The documents linked to an answer. With `onOpen` each is a button that opens
 * it in the surface's viewer; otherwise just its name.
 */
export function LinkedDocumentList({
  documents,
  onOpen,
  className,
}: {
  documents: QuestionDocumentRef[];
  onOpen?: (doc: QuestionDocumentRef) => void;
  className?: string;
}) {
  if (documents.length === 0) return null;
  return (
    <ul className={cn('space-y-1', className)} aria-label="Linked documents">
      {documents.map((doc) => (
        <li key={doc.id} className="flex min-w-0 items-center gap-2 text-sm">
          <Icon name="file" size={14} className="shrink-0 text-ink-3" />
          {onOpen ? (
            <button
              type="button"
              onClick={() => onOpen(doc)}
              className="min-w-0 truncate text-left font-medium text-primary hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            >
              {docName(doc.name)}
            </button>
          ) : (
            <span className="min-w-0 truncate">{docName(doc.name)}</span>
          )}
        </li>
      ))}
    </ul>
  );
}

/** A document the writer may link: the surface's own document list rows. */
export interface LinkableDocument {
  id: string;
  name: string;
  categoryPath?: string[];
}

export interface AnswerComposerProps {
  roomId: string;
  questionId: string;
  /** The documents the writer can see (the surface's document list). */
  library: LinkableDocument[];
  /** The current answer / draft, to edit. */
  initial?: { body: string; documents: QuestionDocumentRef[] } | null;
  /** The textarea's label ("Answer", "Your answer"). */
  label: string;
  submitLabel: string;
  /** Shown under the buttons — what submitting does (e.g. "The buyer sees it at once"). */
  hint?: string;
  pending: boolean;
  /** The last submit's error (a 400 naming refused documents marks them). */
  error: unknown;
  /** Linked documents already known to be hidden from the asking buyer (admin editing a draft) — marked up front. */
  buyerCannotSee?: readonly string[];
  onSubmit: (answer: AnswerQuestionRequest) => void;
  onCancel?: () => void;
  /** A "?" help tip shown after the "Suggest documents" button. */
  suggestAside?: React.ReactNode;
}

type SuggestState =
  | { status: 'idle' }
  | { status: 'loading' }
  | { status: 'loaded'; result: QuestionSuggestionsResponse }
  | { status: 'error'; error: unknown };

/**
 * Write an answer to a buyer question (ADR-0039): the text, and room documents
 * linked to it — picked from the writer's own document list or from "Suggest
 * documents", where the AI FINDS documents that might answer (it never writes
 * the answer). Shared by the intermediary's Q&A queue and the seller portal. The
 * intermediary's answer goes straight to the buyer, so the API refuses (and this
 * marks) any document the asking buyer cannot see; a seller's draft is not
 * checked against the buyer at all — the intermediary sees that at release.
 */
export function AnswerComposer({
  roomId,
  questionId,
  library,
  initial,
  label,
  submitLabel,
  hint,
  pending,
  error,
  buyerCannotSee,
  onSubmit,
  onCancel,
  suggestAside,
}: AnswerComposerProps) {
  const fieldId = React.useId();
  const [body, setBody] = React.useState(initial?.body ?? '');
  const [linked, setLinked] = React.useState<string[]>(
    () => initial?.documents.map((d) => d.id) ?? [],
  );
  const [suggest, setSuggest] = React.useState<SuggestState>({ status: 'idle' });
  const beginSubmit = useSingleSubmit();

  // Names for every id we may show: the library, the existing answer, suggestions.
  const names = React.useMemo(() => {
    const map = new Map<string, string | null>();
    for (const d of initial?.documents ?? []) map.set(d.id, d.name);
    for (const d of library) map.set(d.id, d.name);
    if (suggest.status === 'loaded') {
      for (const d of suggest.result.documents) map.set(d.id, d.name);
    }
    return map;
  }, [initial, library, suggest]);

  const refused = new Set([...refusedDocumentIds(error), ...(buyerCannotSee ?? [])]);
  const atCap = linked.length >= ANSWER_MAX_DOCUMENTS;
  const linkedSet = new Set(linked);
  const options = library
    .filter((d) => !linkedSet.has(d.id))
    .slice()
    .sort((a, b) => a.name.localeCompare(b.name));

  const add = (id: string) =>
    setLinked((prev) =>
      prev.includes(id) || prev.length >= ANSWER_MAX_DOCUMENTS ? prev : [...prev, id],
    );
  const remove = (id: string) => setLinked((prev) => prev.filter((x) => x !== id));

  const loadSuggestions = async () => {
    setSuggest({ status: 'loading' });
    try {
      setSuggest({ status: 'loaded', result: await fetchQuestionSuggestions(roomId, questionId) });
    } catch (err) {
      setSuggest({ status: 'error', error: err });
    }
  };

  const submit = (event: React.FormEvent) => {
    event.preventDefault();
    const text = body.trim();
    if (!text || pending || !beginSubmit(JSON.stringify([text, linked]))) return;
    onSubmit({ body: text, documentIds: linked });
  };

  return (
    <form onSubmit={submit} className="space-y-3" aria-label={label}>
      <div className="space-y-1.5">
        <label htmlFor={fieldId} className="text-sm font-medium">
          {label}
        </label>
        <textarea
          id={fieldId}
          rows={4}
          className={textareaClass}
          value={body}
          maxLength={ANSWER_MAX_CHARS}
          onChange={(e) => setBody(e.target.value)}
          disabled={pending}
        />
      </div>

      <div className="space-y-2">
        <p className="text-sm font-medium">
          Linked documents{' '}
          <span className="font-normal text-muted-foreground">
            ({linked.length} of up to {ANSWER_MAX_DOCUMENTS})
          </span>
        </p>
        {linked.length > 0 ? (
          <ul className="divide-y rounded-md border" aria-label="Documents linked to this answer">
            {linked.map((id) => (
              <li key={id} className="flex items-center gap-2 px-3 py-1.5 text-sm">
                <Icon name="file" size={14} className="shrink-0 text-ink-3" />
                <span className="min-w-0 flex-1 truncate">{docName(names.get(id))}</span>
                {refused.has(id) ? (
                  <span className="shrink-0 text-xs font-semibold text-destructive">
                    Buyer can’t see this
                  </span>
                ) : null}
                <Button
                  type="button"
                  size="sm"
                  variant="ghost"
                  onClick={() => remove(id)}
                  disabled={pending}
                  aria-label={`Remove ${docName(names.get(id))}`}
                >
                  Remove
                </Button>
              </li>
            ))}
          </ul>
        ) : null}
        <div className="flex flex-col gap-2 sm:flex-row">
          <select
            aria-label="Link a document"
            className={selectClass}
            value=""
            onChange={(e) => {
              if (e.target.value) add(e.target.value);
            }}
            disabled={pending || atCap || options.length === 0}
          >
            <option value="">
              {options.length === 0 ? 'No more documents to link' : 'Link a document…'}
            </option>
            {options.map((d) => (
              <option key={d.id} value={d.id}>
                {d.categoryPath && d.categoryPath.length > 0
                  ? `${d.name} — ${categoryPathLabel(d.categoryPath, ' › ')}`
                  : d.name}
              </option>
            ))}
          </select>
          <div className="flex shrink-0 items-center gap-1.5">
            <Button
              type="button"
              variant="outline"
              onClick={() => void loadSuggestions()}
              disabled={suggest.status === 'loading'}
              className="flex-1 sm:flex-none"
            >
              <Icon name="spark" size={14} />
              {suggest.status === 'loading' ? 'Finding…' : 'Suggest documents'}
            </Button>
            {suggestAside}
          </div>
        </div>

        {suggest.status === 'error' ? <ErrorText error={suggest.error} /> : null}
        {suggest.status === 'loaded' ? (
          <div className="space-y-2" role="status" aria-live="polite">
            {suggest.result.message ? (
              <p className="text-sm text-muted-foreground">{suggest.result.message}</p>
            ) : suggest.result.documents.length === 0 ? (
              <p className="text-sm text-muted-foreground">No matching documents found.</p>
            ) : (
              <>
                <p className="text-xs text-muted-foreground">
                  Documents that might answer this question — add the ones that do.
                </p>
                <ul className="divide-y rounded-md border" aria-label="Suggested documents">
                  {suggest.result.documents.map((doc) => {
                    const added = linkedSet.has(doc.id);
                    return (
                      <li key={doc.id} className="flex items-center gap-2 px-3 py-1.5">
                        <span className="flex min-w-0 flex-1 flex-col">
                          <span className="truncate text-sm font-medium">{docName(doc.name)}</span>
                          <span className="truncate text-xs text-muted-foreground">
                            {doc.categoryPath.length > 0
                              ? categoryPathLabel(doc.categoryPath, ' › ')
                              : 'Uncategorized'}
                          </span>
                        </span>
                        <Button
                          type="button"
                          size="sm"
                          variant={added ? 'secondary' : 'outline'}
                          aria-pressed={added}
                          aria-label={`${added ? 'Added' : 'Add'} ${docName(doc.name)}`}
                          onClick={() => (added ? remove(doc.id) : add(doc.id))}
                          disabled={pending || (!added && atCap)}
                        >
                          {added ? (
                            <>
                              <Icon name="check" size={12} />
                              Added
                            </>
                          ) : (
                            'Add'
                          )}
                        </Button>
                      </li>
                    );
                  })}
                </ul>
              </>
            )}
          </div>
        ) : null}
      </div>

      <ErrorText error={error} />

      <div className="flex flex-wrap items-center gap-2">
        <Button type="submit" disabled={pending || !body.trim()}>
          {pending ? 'Saving…' : submitLabel}
        </Button>
        {onCancel ? (
          <Button type="button" variant="ghost" onClick={onCancel} disabled={pending}>
            Cancel
          </Button>
        ) : null}
        {hint ? <span className="text-xs text-muted-foreground">{hint}</span> : null}
      </div>
    </form>
  );
}

export interface AskQuestionFormProps {
  /** The documents the asker can see (their own listing) — for "About a document". */
  documents: { id: string; name: string }[];
  pending: boolean;
  /** The last ask's error. */
  error: unknown;
  /** Send the question; call `onDone` once it is saved (the form then clears). */
  onAsk: (question: AskQuestionRequest, onDone: () => void) => void;
  /** Shown beside the button before sending (who sees the question). */
  hint: string;
  /** Shown after a question was sent. */
  sentMessage: string;
  placeholder?: string;
  /** Disables the whole form (e.g. a buyer-run room with no seller yet). */
  disabled?: boolean;
}

/**
 * Ask a question (ADR-0039) — the text and, optionally, the document it is about.
 * Shared by the buyer portal (a buyer asking the deal team; a buy-side team member
 * asking the seller) and the admin app of a buyer-run room (the admin asking the
 * seller). Which documents may be pointed at is the server's call (the asker's
 * own view); this lists the asker's own document listing.
 */
export function AskQuestionForm({
  documents,
  pending,
  error,
  onAsk,
  hint,
  sentMessage,
  placeholder = 'e.g. Can you share customer revenue concentration for the last three years?',
  disabled = false,
}: AskQuestionFormProps) {
  const fieldId = React.useId();
  const docFieldId = React.useId();
  const [body, setBody] = React.useState('');
  const [documentId, setDocumentId] = React.useState('');
  const [sent, setSent] = React.useState(false);
  const sortedDocs = documents.slice().sort((a, b) => a.name.localeCompare(b.name));
  const busy = pending || disabled;
  const beginSubmit = useSingleSubmit();

  const submit = (event: React.FormEvent) => {
    event.preventDefault();
    const text = body.trim();
    if (!text || busy || !beginSubmit(JSON.stringify([text, documentId]))) return;
    setSent(false);
    onAsk({ body: text, ...(documentId ? { documentId } : {}) }, () => {
      setBody('');
      setDocumentId('');
      setSent(true);
    });
  };

  return (
    <form onSubmit={submit} className="space-y-3" aria-label="Ask a question">
      <div className="space-y-1.5">
        <label htmlFor={fieldId} className="text-sm font-medium">
          Your question
        </label>
        <textarea
          id={fieldId}
          rows={3}
          className={textareaClass}
          placeholder={placeholder}
          value={body}
          maxLength={QUESTION_MAX_CHARS}
          onChange={(e) => {
            setBody(e.target.value);
            setSent(false);
          }}
          disabled={busy}
        />
      </div>
      {sortedDocs.length > 0 ? (
        <div className="space-y-1.5">
          <label htmlFor={docFieldId} className="text-sm font-medium">
            About a document <span className="text-muted-foreground">(optional)</span>
          </label>
          <select
            id={docFieldId}
            className={selectClass}
            value={documentId}
            onChange={(e) => setDocumentId(e.target.value)}
            disabled={busy}
          >
            <option value="">No specific document</option>
            {sortedDocs.map((d) => (
              <option key={d.id} value={d.id}>
                {d.name}
              </option>
            ))}
          </select>
        </div>
      ) : null}
      <ErrorText error={error} />
      <div className="flex flex-wrap items-center gap-3">
        <Button type="submit" disabled={busy || !body.trim()}>
          {pending ? 'Sending…' : 'Send question'}
        </Button>
        {sent ? (
          <span role="status" className="text-sm text-muted-foreground">
            {sentMessage}
          </span>
        ) : (
          <span className="text-xs text-muted-foreground">{hint}</span>
        )}
      </div>
    </form>
  );
}

/**
 * Buyer Questions (ADR-0039): the room's published Q&A, as a reader sees it —
 * the published wording and the linked documents THIS reader can open (the API
 * already filtered them). Never who asked. With `onOpen` each linked document
 * opens in the surface's viewer; `actions` adds per-item controls (the admin's
 * edit / unpublish).
 */
export function PublishedQuestionList({
  questions,
  onOpen,
  actions,
  emptyMessage = 'Nothing has been shared yet.',
}: {
  questions: PublishedQuestionView[];
  onOpen?: (doc: QuestionDocumentRef) => void;
  actions?: (question: PublishedQuestionView) => React.ReactNode;
  emptyMessage?: string;
}) {
  if (questions.length === 0) {
    return <p className="text-sm text-muted-foreground">{emptyMessage}</p>;
  }
  return (
    <ul className="space-y-3" aria-label="Buyer Questions">
      {questions.map((q) => (
        <li key={q.id} className="space-y-2 rounded-md border p-3">
          <p className="whitespace-pre-wrap break-words text-sm font-medium">{q.question}</p>
          <div className="space-y-2 border-t pt-2">
            <p className="text-xs font-semibold text-ink-2">
              Answer · shared {formatDate(q.publishedAt)}
            </p>
            <p className="whitespace-pre-wrap break-words text-sm">{q.answer.body}</p>
            <LinkedDocumentList documents={q.answer.documents} onOpen={onOpen} />
          </div>
          {actions ? <div className="flex flex-wrap items-center gap-2">{actions(q)}</div> : null}
        </li>
      ))}
    </ul>
  );
}
